import { useTranslation } from 'react-i18next'

import { Box, Flex } from '@enechain/ecloud-designsystem'

import { DateNav } from '~/components/balance/FilterCard/DateNav'
import { ToggleSwitch } from '~/components/balance/FilterCard/ToggleSwitch'
import { useGcCountdown } from '~/components/balance/SummaryBar/useGcCountdown'
import { Badge } from '~/components/common/Badge'
import { WEEKDAY_KEYS } from '~/components/common/DayGrid'
import { GcCountdownPill } from '~/components/common/GcCountdownPill'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
  type FilterGridItem,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { GENERATION_DETAIL_SERIES_COLORS } from '~/constants/chartPalette'
import { convertAreaLabel } from '~/models/area'
import { formatDateDisplay } from '~/models/dateTime'
import type { GateCloseFrontier } from '~/models/gateClose'
import { komaNumberTimeRange } from '~/models/komaToTime'
import type {
  BgConfig,
  GenDetailSeriesVisibility,
} from '~/pages/BalanceGenerationDetail/constants'
import { TYPE_I18N_KEYS } from '~/pages/BalanceGenerationDetail/constants'

/** 開閉の保存キー (CollapsibleFilterToolbar が接頭辞を足す)。 */
export const GENERATION_DETAIL_FILTER_STORAGE_KEY = 'balanceGenerationDetail'

/** 基本表示トグルで切替可能な系列 (発電形式トグルは #2807 で追加予定)。 */
type BasicSeriesKey = 'genPlan' | 'genActual'

// 色は [[GENERATION_DETAIL_SERIES_COLORS]] から引く (= DetailChart の描画色と同じ出どころ)。
// design `.switch-item[data-color]` は switch の ON 色をチャート凡例色に揃える作法。
const BASIC_TOGGLES: Array<{
  key: BasicSeriesKey
  labelKey: string
  color: string
}> = [
  {
    key: 'genPlan',
    labelKey: 'page:balanceGenerationDetail.filter.genPlan',
    color: GENERATION_DETAIL_SERIES_COLORS.genPlan,
  },
  {
    key: 'genActual',
    labelKey: 'page:balanceGenerationDetail.filter.genActual',
    color: GENERATION_DETAIL_SERIES_COLORS.genActual,
  },
]

type Props = {
  displayDate: Date
  onDateChange: (date: Date) => void
  onToday: () => void
  bgConfig: BgConfig
  seriesVisibility: Pick<GenDetailSeriesVisibility, BasicSeriesKey>
  onToggleSeries: (key: BasicSeriesKey) => void
  /** 基本表示 (計画線 / 実績線) を既定 (両方 ON) へ戻す。 表示日は戻さない。 */
  onClearSeries: () => void
  /** 次の GC のカウントダウンの元 (当日だけ出す。 AC-BDH-019b)。 */
  gateCloseFrontier: GateCloseFrontier | undefined
  gcKoma: number
}

/**
 * 次の GC のカウントダウン (AC-BDH-019b)。 閉じていても見えるよう、 ツールバーの右側 (クリアの左) に置く。
 * 当日以外は何も出さない。
 */
const NextGc = ({
  displayDate,
  frontier,
  gcKoma,
}: {
  displayDate: Date
  frontier: GateCloseFrontier | undefined
  gcKoma: number
}): React.ReactElement | null => {
  const { t } = useTranslation()
  const gcCountdown = useGcCountdown(frontier, displayDate)
  if (gcCountdown === null) return null
  return (
    <GcCountdownPill
      remaining={gcCountdown.text}
      slot={{
        koma: gcKoma + 1,
        range: komaNumberTimeRange(
          gcKoma + 1,
          t('common:gcCountdown.rangeSeparator'),
        ),
      }}
      urgent={gcCountdown.isUrgent}
    />
  )
}

/**
 * 発電詳細の開閉式フィルター (UI 辞書 toolbar-collapsible.md「開閉式」)。
 * 選ぶ部品はすべて開いたカードに置き、 閉じている間は要約で今の値を読む。
 *
 * - カード / 要約の並び = 表示日 → BG → 基本表示
 * - 件数・クリア = 基本表示 (計画値 / 実績値) だけ。 表示日は数えず戻さない。 BG は URL で決まる表示対象で条件ではない
 * - 「時間前入札へ」「発電所・蓄電池の内訳」は画面の操作の行 (カードの外)
 */
export const FilterCard = ({
  displayDate,
  onDateChange,
  onToday,
  bgConfig: bg,
  seriesVisibility,
  onToggleSeries,
  onClearSeries,
  gateCloseFrontier,
  gcKoma,
}: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()

  const dateLabel = t('page:balanceGenerationDetail.filter.displayDate')
  const basicLabel = t('page:balanceGenerationDetail.filter.basicSeries')
  const bgLabel = t('page:balanceGenerationDetail.filter.bg')
  const onSeries = BASIC_TOGGLES.filter((item) => seriesVisibility[item.key])
  const isSeriesChanged = onSeries.length !== BASIC_TOGGLES.length
  const bgValue = `${bg.name} (${bg.code})`

  const summary: ToolbarStatusItem[] = [
    {
      key: 'date',
      label: dateLabel,
      // 日付欄の閉じた表示と同じ `YYYY/MM/DD (曜)`
      value: t('common:date.withWeekday', {
        date: formatDateDisplay(displayDate, i18n.language),
        weekday: t(
          `common:date.calendar.weekday.${WEEKDAY_KEYS[displayDate.getDay()]}`,
        ),
      }),
    },
    { key: 'bg', label: bgLabel, value: bgValue },
    // 見え方の設定は既定 (すべて表示) から変えた時だけ出す (UI 辞書 toolbar-collapsible.md の要約。 需要詳細と同じ)
    ...(isSeriesChanged
      ? [
          {
            key: 'basicSeries',
            label: basicLabel,
            value:
              onSeries
                .map((item) => t(item.labelKey))
                .join(t('common:filter.listSeparator')) || '—',
          },
        ]
      : []),
  ]

  const items: FilterGridItem[] = [
    {
      key: 'date',
      // ラベルは DateNav が持つ (横並び)
      control: (
        <DateNav
          displayDate={displayDate}
          labelKeyPrefix="page:balanceGenerationDetail.filter"
          layout="horizontal"
          onDateChange={onDateChange}
          onToday={onToday}
        />
      ),
    },
    {
      key: 'bg',
      label: bgLabel,
      // BG のチップ群。 design hatsu-detail.html L76-79 はいずれも `.badge.badge-neutral`
      // (= DS `Badge status="neutral"`)。 寸法・書体は `~/constants/system.ts` の badge recipe 由来。
      // 並び順は design 実測 (コード → エリア → 地域 → 種別)。
      // [design 差異] design にある「地域」チップは dev に対応データが無く未実装 (別 issue)。
      // 調達種別チップは design に無い dev 独自の追加。
      control: (
        <Flex alignItems="center" flexWrap="wrap" gap={2}>
          <Badge css={{ fontVariantNumeric: 'tabular-nums' }} status="neutral">
            {bg.code}
          </Badge>
          <Badge status="neutral">{convertAreaLabel(bg.areaCode)}</Badge>
          {/* procurementType は GetGenerationDetail 未露出 (#2000)。空チップを出さない */}
          {bg.procurementType !== '' && (
            <Badge status="neutral">{bg.procurementType}</Badge>
          )}
          <Badge status="neutral">
            {t(TYPE_I18N_KEYS[bg.type] ?? bg.type)}
          </Badge>
        </Flex>
      ),
    },
    {
      key: 'basicSeries',
      label: basicLabel,
      control: (
        <Flex alignItems="center" flexWrap="wrap" gap={3}>
          {BASIC_TOGGLES.map((item) => (
            <ToggleSwitch
              key={item.key}
              isOn={seriesVisibility[item.key]}
              label={t(item.labelKey)}
              onColor={item.color}
              onToggle={() => onToggleSeries(item.key)}
            />
          ))}
        </Flex>
      ),
    },
  ]

  return (
    <Box flexShrink={0} marginBottom="2px">
      <CollapsibleFilterToolbar
        modifiedCount={isSeriesChanged ? 1 : 0}
        onClear={onClearSeries}
        storageKey={GENERATION_DETAIL_FILTER_STORAGE_KEY}
        summary={summary}
        trailing={
          <NextGc
            displayDate={displayDate}
            frontier={gateCloseFrontier}
            gcKoma={gcKoma}
          />
        }
      >
        <FilterGrid items={items} />
      </CollapsibleFilterToolbar>
    </Box>
  )
}
